import React, { useState } from 'react';
import { useRituals } from './RitualContext';
import { Trophy, Medal, Star, Flame, Gift, Calendar, Heart, Diamond, Sparkles, BadgeCheck, Crown, Zap, Clock, CheckCircle } from 'lucide-react';
export const RitualAchievements: React.FC = () => {
  const {
    streakCount,
    weeklyStreakCount,
    todayCompletedCount,
    totalDailyRituals
  } = useRituals();
  const [activeTab, setActiveTab] = useState<'rituals' | 'streaks' | 'special' | 'leaderboards'>('rituals');
  // Achievement data
  const ritualAchievements = [{
    id: 'ritual-1',
    title: 'Ritual Novice',
    description: 'Complete 10 daily rituals',
    progress: 10,
    target: 10,
    completed: true,
    icon: 'Trophy',
    reward: '25 💗'
  }, {
    id: 'ritual-2',
    title: 'Ritual Adept',
    description: 'Complete 50 daily rituals',
    progress: 32,
    target: 50,
    completed: false,
    icon: 'Trophy',
    reward: '100 💗'
  }, {
    id: 'ritual-3',
    title: 'Ritual Master',
    description: 'Complete 30 daily rituals in a row',
    progress: 12,
    target: 30,
    completed: false,
    icon: 'Trophy',
    reward: '250 💗'
  }, {
    id: 'ritual-4',
    title: 'Perfect Day',
    description: 'Complete all daily rituals in one day',
    progress: todayCompletedCount,
    target: totalDailyRituals,
    completed: todayCompletedCount >= totalDailyRituals,
    icon: 'CheckCircle',
    reward: '50 💗'
  }, {
    id: 'ritual-5',
    title: 'Lootbox Legend',
    description: 'Unlock 3 weekly lootboxes in a row',
    progress: 1,
    target: 3,
    completed: false,
    icon: 'Gift',
    reward: '100 💗 + Special Lootbox'
  }, {
    id: 'ritual-6',
    title: 'Gem Hunter',
    description: 'Complete 5 monetization rituals',
    progress: 2,
    target: 5,
    completed: false,
    icon: 'Diamond',
    reward: '50 💎'
  }];
  const streakAchievements = [{
    id: 'streak-1',
    title: 'Streak Starter',
    description: 'Maintain a 3-day ritual streak',
    progress: Math.min(streakCount, 3),
    target: 3,
    completed: streakCount >= 3,
    icon: 'Flame',
    reward: '30 💗'
  }, {
    id: 'streak-2',
    title: 'Streak Warrior',
    description: 'Maintain a 7-day ritual streak',
    progress: Math.min(streakCount, 7),
    target: 7,
    completed: streakCount >= 7,
    icon: 'Flame',
    reward: '100 💗'
  }, {
    id: 'streak-3',
    title: 'Streak Master',
    description: 'Maintain a 30-day ritual streak',
    progress: Math.min(streakCount, 30),
    target: 30,
    completed: streakCount >= 30,
    icon: 'Flame',
    reward: '500 💗 + Exclusive Badge'
  }, {
    id: 'streak-4',
    title: 'Weekly Devotion',
    description: 'Complete 4 weekly ritual sets',
    progress: Math.min(weeklyStreakCount, 4),
    target: 4,
    completed: weeklyStreakCount >= 4,
    icon: 'Calendar',
    reward: '200 💗'
  }, {
    id: 'streak-5',
    title: 'Monthly Dedication',
    description: 'Complete all rituals for a full month',
    progress: 21,
    target: 30,
    completed: false,
    icon: 'Calendar',
    reward: '1000 💗 + VIP Month'
  }];
  const specialAchievements = [{
    id: 'special-1',
    title: 'Kinky Explorer',
    description: 'Complete 10 fun rotating rituals',
    progress: 6,
    target: 10,
    completed: false,
    icon: 'Sparkles',
    reward: '100 💗'
  }, {
    id: 'special-2',
    title: "Angel's Favorite",
    description: 'Send 50 gifts to Angels',
    progress: 23,
    target: 50,
    completed: false,
    icon: 'Heart',
    reward: 'Special Angel Interaction'
  }, {
    id: 'special-3',
    title: 'Premium Supporter',
    description: 'Complete 10 premium rituals',
    progress: 3,
    target: 10,
    completed: false,
    icon: 'Diamond',
    reward: '200 💎'
  }, {
    id: 'special-4',
    title: 'Event Master',
    description: 'Complete 5 special event ritual sets',
    progress: 2,
    target: 5,
    completed: false,
    icon: 'Star',
    reward: 'Exclusive Event Badge'
  }, {
    id: 'special-5',
    title: 'Collection Complete',
    description: 'Earn all ritual badges',
    progress: 8,
    target: 15,
    completed: false,
    icon: 'BadgeCheck',
    reward: 'Ultimate Ritual Master Title'
  }];
  const renderAchievementItem = (achievement: any) => {
    const progressPercentage = achievement.progress / achievement.target * 100;
    return <div key={achievement.id} className={`p-4 ${achievement.completed ? 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border-amber-200 dark:border-amber-800/30' : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800'} rounded-lg border shadow-sm hover:shadow-md transition-all duration-200`} data-id="element-17500">
        <div className="flex items-start gap-4" data-id="element-17501">
          <div className={`w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center ${achievement.completed ? 'bg-gradient-to-r from-amber-400 to-yellow-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'}`} data-id="element-17502">
            {achievement.icon === 'Trophy' && <Trophy size={24} data-id="element-17503" />}
            {achievement.icon === 'Flame' && <Flame size={24} data-id="element-17504" />}
            {achievement.icon === 'Gift' && <Gift size={24} data-id="element-17505" />}
            {achievement.icon === 'Calendar' && <Calendar size={24} data-id="element-17506" />}
            {achievement.icon === 'Heart' && <Heart size={24} data-id="element-17507" />}
            {achievement.icon === 'Diamond' && <Diamond size={24} data-id="element-17508" />}
            {achievement.icon === 'Sparkles' && <Sparkles size={24} data-id="element-17509" />}
            {achievement.icon === 'Star' && <Star size={24} data-id="element-17510" />}
            {achievement.icon === 'BadgeCheck' && <BadgeCheck size={24} data-id="element-17511" />}
            {achievement.icon === 'CheckCircle' && <CheckCircle size={24} data-id="element-17512" />}
          </div>
          <div className="flex-1" data-id="element-17513">
            <div className="flex items-center justify-between" data-id="element-17514">
              <h3 className="font-bold text-base flex items-center gap-1.5" data-id="element-17515">
                {achievement.title}
                {achievement.completed && <span className="text-amber-500" data-id="element-17516">
                    <BadgeCheck size={16} data-id="element-17517" />
                  </span>}
              </h3>
              <div className="text-sm font-semibold text-pink-600 dark:text-pink-400" data-id="element-17518">
                {achievement.reward}
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 mb-2" data-id="element-17519">
              {achievement.description}
            </p>
            <div className="mt-2" data-id="element-17520">
              <div className="flex items-center justify-between mb-1" data-id="element-17521">
                <span className="text-xs text-gray-500 dark:text-gray-500" data-id="element-17522">
                  Progress
                </span>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-17523">
                  {achievement.progress}/{achievement.target}
                </span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-17524">
                <div className={`h-full rounded-full transition-all duration-500 ${achievement.completed ? 'bg-gradient-to-r from-amber-400 to-yellow-500' : 'bg-gradient-to-r from-pink-500 to-purple-500'}`} style={{
                width: `${progressPercentage}%`
              }} data-id="element-17525"></div>
              </div>
            </div>
            {achievement.completed && <div className="mt-3 flex items-center justify-between" data-id="element-17526">
                <div className="flex items-center gap-1.5" data-id="element-17527">
                  <CheckCircle size={16} className="text-green-500" data-id="element-17528" />
                  <span className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-17529">
                    Completed
                  </span>
                </div>
                {achievement.id === 'ritual-1' || achievement.id === 'streak-1' ? <div className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full" data-id="element-17530">
                    Claimed
                  </div> : <button className="text-xs bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 px-2 py-1 rounded-full hover:bg-pink-200 dark:hover:bg-pink-800/40 transition-all duration-200" data-id="element-17531">
                    Claim Reward
                  </button>}
              </div>}
          </div>
        </div>
      </div>;
  };
  return <div className="w-full max-w-4xl mx-auto" data-id="element-17532">
      {/* Header with badges showcase */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-6 text-white shadow-lg mb-6" data-id="element-17533">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-17534">
          <div data-id="element-17535">
            <h2 className="text-xl font-bold mb-1" data-id="element-17536">Ritual Achievements</h2>
            <p className="text-white/80 text-sm" data-id="element-17537">
              Complete rituals to earn badges, rewards, and bragging rights
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-17538">
            <div className="bg-white/20 rounded-full p-2" data-id="element-17539">
              <Trophy size={24} className="text-amber-300" data-id="element-17540" />
            </div>
            <div className="bg-white/20 rounded-full p-2" data-id="element-17541">
              <Flame size={24} className="text-red-400" data-id="element-17542" />
            </div>
            <div className="bg-white/20 rounded-full p-2" data-id="element-17543">
              <Crown size={24} className="text-yellow-300" data-id="element-17544" />
            </div>
            <div className="bg-white/20 rounded-full p-2 relative opacity-50" data-id="element-17545">
              <Star size={24} className="text-white" data-id="element-17546" />
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-17547">
                <Lock size={14} className="text-white" data-id="element-17548" />
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 bg-white/10 rounded-lg p-3" data-id="element-17549">
          <div className="flex items-center justify-between mb-2" data-id="element-17550">
            <h3 className="text-sm font-medium flex items-center gap-1.5" data-id="element-17551">
              <Zap size={16} data-id="element-17552" />
              <span data-id="element-17553">Your Achievement Stats</span>
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-17554">
            <div className="bg-white/10 rounded-lg p-2 text-center" data-id="element-17555">
              <div className="text-2xl font-bold" data-id="element-17556">3/15</div>
              <div className="text-xs text-white/80" data-id="element-17557">Badges Earned</div>
            </div>
            <div className="bg-white/10 rounded-lg p-2 text-center" data-id="element-17558">
              <div className="text-2xl font-bold" data-id="element-17559">{streakCount}</div>
              <div className="text-xs text-white/80" data-id="element-17560">Day Streak</div>
            </div>
            <div className="bg-white/10 rounded-lg p-2 text-center" data-id="element-17561">
              <div className="text-2xl font-bold" data-id="element-17562">42</div>
              <div className="text-xs text-white/80" data-id="element-17563">Rituals Completed</div>
            </div>
            <div className="bg-white/10 rounded-lg p-2 text-center" data-id="element-17564">
              <div className="text-2xl font-bold" data-id="element-17565">675</div>
              <div className="text-xs text-white/80" data-id="element-17566">Tokens Earned</div>
            </div>
          </div>
        </div>
      </div>
      {/* Tabs for different achievement types */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide" data-id="element-17567">
        <button className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1.5 ${activeTab === 'rituals' ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800'}`} onClick={() => setActiveTab('rituals')} data-id="element-17568">
          <Trophy size={16} data-id="element-17569" />
          <span data-id="element-17570">Ritual Badges</span>
        </button>
        <button className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1.5 ${activeTab === 'streaks' ? 'bg-gradient-to-r from-amber-500 to-red-500 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800'}`} onClick={() => setActiveTab('streaks')} data-id="element-17571">
          <Flame size={16} data-id="element-17572" />
          <span data-id="element-17573">Streak Badges</span>
        </button>
        <button className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1.5 ${activeTab === 'special' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800'}`} onClick={() => setActiveTab('special')} data-id="element-17574">
          <Star size={16} data-id="element-17575" />
          <span data-id="element-17576">Special Badges</span>
        </button>
        <button className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1.5 ${activeTab === 'leaderboards' ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800'}`} onClick={() => setActiveTab('leaderboards')} data-id="element-17577">
          <Medal size={16} data-id="element-17578" />
          <span data-id="element-17579">Leaderboards</span>
        </button>
      </div>
      {/* Achievement list */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-17580">
        {activeTab === 'rituals' && ritualAchievements.map(achievement => renderAchievementItem(achievement))}
        {activeTab === 'streaks' && streakAchievements.map(achievement => renderAchievementItem(achievement))}
        {activeTab === 'special' && specialAchievements.map(achievement => renderAchievementItem(achievement))}
        {activeTab === 'leaderboards' && <>
            <div className="md:col-span-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm p-4" data-id="element-17581">
              <h3 className="font-bold text-lg flex items-center gap-2 mb-4" data-id="element-17582">
                <Medal size={20} className="text-amber-500" data-id="element-17583" />
                <span data-id="element-17584">Global Ritual Leaderboards</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-17585">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800/30 p-3" data-id="element-17586">
                  <h4 className="font-medium text-sm mb-3 flex items-center gap-1.5" data-id="element-17587">
                    <Trophy size={16} className="text-blue-600" data-id="element-17588" />
                    <span data-id="element-17589">Daily Ritual Champions</span>
                  </h4>
                  <div className="space-y-2" data-id="element-17590">
                    <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg" data-id="element-17591">
                      <div className="flex items-center gap-2" data-id="element-17592">
                        <div className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400 text-xs font-bold" data-id="element-17593">
                          1
                        </div>
                        <span className="text-sm" data-id="element-17594">SweetAngel92</span>
                      </div>
                      <span className="text-xs font-medium" data-id="element-17595">30/30</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg" data-id="element-17596">
                      <div className="flex items-center gap-2" data-id="element-17597">
                        <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400 text-xs font-bold" data-id="element-17598">
                          2
                        </div>
                        <span className="text-sm" data-id="element-17599">RitualMaster</span>
                      </div>
                      <span className="text-xs font-medium" data-id="element-17600">29/30</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg" data-id="element-17601">
                      <div className="flex items-center gap-2" data-id="element-17602">
                        <div className="w-6 h-6 rounded-full bg-amber-800/20 flex items-center justify-center text-amber-800/60 dark:text-amber-400/60 text-xs font-bold" data-id="element-17603">
                          3
                        </div>
                        <span className="text-sm" data-id="element-17604">KinkQueen</span>
                      </div>
                      <span className="text-xs font-medium" data-id="element-17605">28/30</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-100 dark:border-pink-800/30" data-id="element-17606">
                      <div className="flex items-center gap-2" data-id="element-17607">
                        <div className="w-6 h-6 rounded-full bg-pink-100 dark:bg-pink-800/50 flex items-center justify-center text-pink-600 dark:text-pink-400 text-xs font-bold" data-id="element-17608">
                          24
                        </div>
                        <span className="text-sm" data-id="element-17609">You</span>
                      </div>
                      <span className="text-xs font-medium" data-id="element-17610">
                        {todayCompletedCount}/30
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg border border-amber-200 dark:border-amber-800/30 p-3" data-id="element-17611">
                  <h4 className="font-medium text-sm mb-3 flex items-center gap-1.5" data-id="element-17612">
                    <Flame size={16} className="text-amber-600" data-id="element-17613" />
                    <span data-id="element-17614">Streak Leaders</span>
                  </h4>
                  <div className="space-y-2" data-id="element-17615">
                    <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg" data-id="element-17616">
                      <div className="flex items-center gap-2" data-id="element-17617">
                        <div className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400 text-xs font-bold" data-id="element-17618">
                          1
                        </div>
                        <span className="text-sm" data-id="element-17619">DailyDevotee</span>
                      </div>
                      <span className="text-xs font-medium" data-id="element-17620">98 days 🔥</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg" data-id="element-17621">
                      <div className="flex items-center gap-2" data-id="element-17622">
                        <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400 text-xs font-bold" data-id="element-17623">
                          2
                        </div>
                        <span className="text-sm" data-id="element-17624">AngelLover69</span>
                      </div>
                      <span className="text-xs font-medium" data-id="element-17625">87 days 🔥</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg" data-id="element-17626">
                      <div className="flex items-center gap-2" data-id="element-17627">
                        <div className="w-6 h-6 rounded-full bg-amber-800/20 flex items-center justify-center text-amber-800/60 dark:text-amber-400/60 text-xs font-bold" data-id="element-17628">
                          3
                        </div>
                        <span className="text-sm" data-id="element-17629">RitualKing</span>
                      </div>
                      <span className="text-xs font-medium" data-id="element-17630">72 days 🔥</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-100 dark:border-pink-800/30" data-id="element-17631">
                      <div className="flex items-center gap-2" data-id="element-17632">
                        <div className="w-6 h-6 rounded-full bg-pink-100 dark:bg-pink-800/50 flex items-center justify-center text-pink-600 dark:text-pink-400 text-xs font-bold" data-id="element-17633">
                          156
                        </div>
                        <span className="text-sm" data-id="element-17634">You</span>
                      </div>
                      <span className="text-xs font-medium" data-id="element-17635">
                        {streakCount} days 🔥
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg border border-purple-200 dark:border-purple-800/30 p-3" data-id="element-17636">
                  <h4 className="font-medium text-sm mb-3 flex items-center gap-1.5" data-id="element-17637">
                    <Diamond size={16} className="text-purple-600" data-id="element-17638" />
                    <span data-id="element-17639">Premium Supporters</span>
                  </h4>
                  <div className="space-y-2" data-id="element-17640">
                    <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg" data-id="element-17641">
                      <div className="flex items-center gap-2" data-id="element-17642">
                        <div className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400 text-xs font-bold" data-id="element-17643">
                          1
                        </div>
                        <span className="text-sm" data-id="element-17644">DiamondKing</span>
                      </div>
                      <span className="text-xs font-medium" data-id="element-17645">5,240 💎</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg" data-id="element-17646">
                      <div className="flex items-center gap-2" data-id="element-17647">
                        <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400 text-xs font-bold" data-id="element-17648">
                          2
                        </div>
                        <span className="text-sm" data-id="element-17649">GemCollector</span>
                      </div>
                      <span className="text-xs font-medium" data-id="element-17650">4,875 💎</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg" data-id="element-17651">
                      <div className="flex items-center gap-2" data-id="element-17652">
                        <div className="w-6 h-6 rounded-full bg-amber-800/20 flex items-center justify-center text-amber-800/60 dark:text-amber-400/60 text-xs font-bold" data-id="element-17653">
                          3
                        </div>
                        <span className="text-sm" data-id="element-17654">VIPLover</span>
                      </div>
                      <span className="text-xs font-medium" data-id="element-17655">3,920 💎</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-100 dark:border-pink-800/30" data-id="element-17656">
                      <div className="flex items-center gap-2" data-id="element-17657">
                        <div className="w-6 h-6 rounded-full bg-pink-100 dark:bg-pink-800/50 flex items-center justify-center text-pink-600 dark:text-pink-400 text-xs font-bold" data-id="element-17658">
                          87
                        </div>
                        <span className="text-sm" data-id="element-17659">You</span>
                      </div>
                      <span className="text-xs font-medium" data-id="element-17660">240 💎</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="md:col-span-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm p-4" data-id="element-17661">
              <div className="flex items-center justify-between mb-4" data-id="element-17662">
                <h3 className="font-bold text-lg flex items-center gap-2" data-id="element-17663">
                  <Clock size={20} className="text-blue-500" data-id="element-17664" />
                  <span data-id="element-17665">Ritual Season Rankings</span>
                </h3>
                <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-1 rounded-full text-xs font-medium" data-id="element-17666">
                  Season 2: 18 days left
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-17667">
                Complete rituals to climb the seasonal leaderboard and earn
                exclusive rewards
              </p>
              <div className="overflow-x-auto" data-id="element-17668">
                <table className="w-full" data-id="element-17669">
                  <thead data-id="element-17670">
                    <tr className="border-b border-gray-200 dark:border-gray-800" data-id="element-17671">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-17672">
                        Rank
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-17673">
                        User
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-17674">
                        Rituals
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-17675">
                        Streak
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-17676">
                        Points
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-17677">
                        Reward
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-17678">
                    <tr className="border-b border-gray-200 dark:border-gray-800" data-id="element-17679">
                      <td className="px-4 py-3 text-sm" data-id="element-17680">
                        <div className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400 text-xs font-bold" data-id="element-17681">
                          1
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-17682">RitualMaster</td>
                      <td className="px-4 py-3 text-sm" data-id="element-17683">247</td>
                      <td className="px-4 py-3 text-sm" data-id="element-17684">98 days</td>
                      <td className="px-4 py-3 text-sm font-medium" data-id="element-17685">12,540</td>
                      <td className="px-4 py-3 text-sm" data-id="element-17686">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-xs rounded-full" data-id="element-17687">
                          <Crown size={12} data-id="element-17688" />
                          <span data-id="element-17689">Legendary</span>
                        </span>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200 dark:border-gray-800" data-id="element-17690">
                      <td className="px-4 py-3 text-sm" data-id="element-17691">
                        <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400 text-xs font-bold" data-id="element-17692">
                          2
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-17693">AngelLover69</td>
                      <td className="px-4 py-3 text-sm" data-id="element-17694">231</td>
                      <td className="px-4 py-3 text-sm" data-id="element-17695">87 days</td>
                      <td className="px-4 py-3 text-sm font-medium" data-id="element-17696">11,875</td>
                      <td className="px-4 py-3 text-sm" data-id="element-17697">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs rounded-full" data-id="element-17698">
                          <Diamond size={12} data-id="element-17699" />
                          <span data-id="element-17700">Epic</span>
                        </span>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200 dark:border-gray-800" data-id="element-17701">
                      <td className="px-4 py-3 text-sm" data-id="element-17702">
                        <div className="w-6 h-6 rounded-full bg-amber-800/20 flex items-center justify-center text-amber-800/60 dark:text-amber-400/60 text-xs font-bold" data-id="element-17703">
                          3
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-17704">KinkQueen</td>
                      <td className="px-4 py-3 text-sm" data-id="element-17705">215</td>
                      <td className="px-4 py-3 text-sm" data-id="element-17706">72 days</td>
                      <td className="px-4 py-3 text-sm font-medium" data-id="element-17707">10,920</td>
                      <td className="px-4 py-3 text-sm" data-id="element-17708">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs rounded-full" data-id="element-17709">
                          <Star size={12} data-id="element-17710" />
                          <span data-id="element-17711">Rare</span>
                        </span>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200 dark:border-gray-800 bg-pink-50 dark:bg-pink-900/10" data-id="element-17712">
                      <td className="px-4 py-3 text-sm" data-id="element-17713">
                        <div className="w-6 h-6 rounded-full bg-pink-100 dark:bg-pink-800/50 flex items-center justify-center text-pink-600 dark:text-pink-400 text-xs font-bold" data-id="element-17714">
                          42
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium" data-id="element-17715">You</td>
                      <td className="px-4 py-3 text-sm" data-id="element-17716">
                        {todayCompletedCount * 7 + 12}
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-17717">{streakCount} days</td>
                      <td className="px-4 py-3 text-sm font-medium" data-id="element-17718">3,240</td>
                      <td className="px-4 py-3 text-sm" data-id="element-17719">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-green-500 to-teal-500 text-white text-xs rounded-full" data-id="element-17720">
                          <BadgeCheck size={12} data-id="element-17721" />
                          <span data-id="element-17722">Common</span>
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>}
      </div>
    </div>;
};
// Temporary component for the Lock icon since it wasn't imported at the top
const Lock = ({
  size,
  className
}: {
  size: number;
  className: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-17723">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" data-id="element-17724"></rect>
    <path d="M7 11V7a5 5 0 0 1 10 0v4" data-id="element-17725"></path>
  </svg>;